// ============================================================
// GAS WebアプリURL
// ============================================================
const GAS_ENDPOINT =
  'https://script.google.com/macros/s/AKfycbwMIUr0pXSiubpQ4w7mYoO8WE9RTanN2b1ZAcwPiqky0dh4K7oPvivWlo8asOBPuJPi/exec';

// ============================================================
// ストレージキー定数
// ============================================================
/** 全LPで共通のvisitor_id保存キー（同一ドメインで同一ユーザー識別） */
const VISITOR_ID_KEY = 'lp_common_visitor_id';
/** UTMパラメータ保存用sessionStorageキー */
const SESSION_UTM_KEY = 'lp_utm_params';
/** アフィリエイトパラメータ保存用sessionStorageキー */
const SESSION_AFF_KEY = 'lp_aff_params';

// ============================================================
// 全DBフィールド定義
// ============================================================
export const ALL_DB_FIELDS = [
  'qualification',
  'birth_year',
  'birth_month',
  'birth_day',
  'name',
  'phone',
  // 2026-08-10 追加。gas-questionnaire の buildAiWebLeadPayload_ が
  // そのまま AIシート「LP完結リード」のW列(メール)へ転送する。
  'email',
  'postal_code',
  'prefecture',
  'work_style',
  'transfer_timing',
] as const;

export type DbField = (typeof ALL_DB_FIELDS)[number];
export type PersonalPayload = Record<DbField, string>;

// ============================================================
// このLPで取得するフォームフィールド（LP固有設定）
// ============================================================
export const FORM_FIELDS: DbField[] = [
  'qualification',
  'work_style',
  'postal_code',
  'name',
  'phone',
  // 2026-08-10 追加（転職希望時期・生年月日・メールアドレス）
  'transfer_timing',
  'birth_year',
  'birth_month',
  'birth_day',
  'email',
];

// ============================================================
// UUID v4 生成
// ============================================================
function generateUuidV4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ============================================================
// 【機能1】visitor_id の生成・管理
// ページ表示時（DOMContentLoaded）に即座に生成・保存
// ============================================================
export function getOrCreateVisitorId(): string {
  try {
    const existing = localStorage.getItem(VISITOR_ID_KEY);
    if (existing) return existing;
    const id = `vis_${generateUuidV4()}`;
    localStorage.setItem(VISITOR_ID_KEY, id);
    return id;
  } catch {
    return `vis_${generateUuidV4()}`;
  }
}

// ============================================================
// 【機能2】conversion_id の生成（フォーム送信時に生成）
// ============================================================
export function generateConversionId(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const yyyymmdd = `${yyyy}${mm}${dd}`;
  const random = Math.random().toString(36).substring(2, 10).padEnd(8, '0').substring(0, 8);
  return `cvid_${yyyymmdd}_${random}`;
}

// ============================================================
// lp_id の自動取得（URLから生成・定数で設定しない）
// ============================================================
export function getLpId(): string {
  const origin = window.location.origin;
  let pathname = window.location.pathname;
  if (!pathname.endsWith('/')) pathname += '/';
  return `${origin}${pathname}`;
}

// ============================================================
// UTM・アフィリエイトパラメータをsessionStorageに保存
// ページ表示時に呼び出し、URLが変わっても初期値を保持
// ============================================================
export function saveParamsToSession(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {
      utm_source: params.get('utm_source') ?? '',
      utm_medium: params.get('utm_medium') ?? '',
      utm_campaign: params.get('utm_campaign') ?? '',
      utm_term: params.get('utm_term') ?? '',
      utm_content: params.get('utm_content') ?? '',
    };
    const aff = {
      aff_company: params.get('aff_company') ?? '',
      aff_id: params.get('aff_id') ?? '',
      click_id: params.get('click_id') ?? '',
    };
    // 実際のパラメータがある場合のみ保存（空で上書きしない）
    if (Object.values(utm).some((v) => v !== '')) {
      sessionStorage.setItem(SESSION_UTM_KEY, JSON.stringify(utm));
    }
    if (Object.values(aff).some((v) => v !== '')) {
      sessionStorage.setItem(SESSION_AFF_KEY, JSON.stringify(aff));
    }
  } catch {
    // sessionStorage 利用不可の場合は無視
  }
}

// ============================================================
// UTMパラメータ取得（sessionStorageから読み取り、フォールバックはURL）
// ============================================================
export function getUTMParams(): Record<string, string> {
  try {
    const stored = sessionStorage.getItem(SESSION_UTM_KEY);
    if (stored) return JSON.parse(stored) as Record<string, string>;
  } catch {
    // フォールバック
  }
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') ?? '',
    utm_medium: params.get('utm_medium') ?? '',
    utm_campaign: params.get('utm_campaign') ?? '',
    utm_term: params.get('utm_term') ?? '',
    utm_content: params.get('utm_content') ?? '',
  };
}

// ============================================================
// アフィリエイトパラメータ取得（sessionStorageから読み取り）
// ============================================================
export function getAffiliateParams(): Record<string, string> {
  try {
    const stored = sessionStorage.getItem(SESSION_AFF_KEY);
    if (stored) return JSON.parse(stored) as Record<string, string>;
  } catch {
    // フォールバック
  }
  const params = new URLSearchParams(window.location.search);
  return {
    aff_company: params.get('aff_company') ?? '',
    aff_id: params.get('aff_id') ?? '',
    click_id: params.get('click_id') ?? '',
  };
}

// ============================================================
// 電話番号の正規化（ハイフン除去、11桁チェック）
// ============================================================
export function normalizePhone(raw: string): { value: string; error: string } {
  const value = raw.replace(/[^0-9]/g, '');
  if (value.length !== 11) return { value, error: '電話番号は11桁で入力してください' };
  return { value, error: '' };
}

// ============================================================
// 郵便番号の正規化（ハイフン除去、7桁チェック）
// ============================================================
export function normalizePostalCode(raw: string): { value: string; error: string } {
  const value = raw.replace(/[^0-9]/g, '');
  if (value.length !== 7) return { value, error: '郵便番号は7桁で入力してください' };
  return { value, error: '' };
}

// ============================================================
// 【機能3】送信ペイロード構築
// ============================================================
export function buildPayload(
  personalData: Partial<PersonalPayload>,
  formFields: DbField[] = FORM_FIELDS
): Record<string, unknown> {
  // 全DBフィールドをデフォルト空文字で初期化
  const personalPayload = Object.fromEntries(
    ALL_DB_FIELDS.map((f) => [f, ''])
  ) as PersonalPayload;

  // FORM_FIELDSに含まれるフィールドのみ実際の値を設定
  formFields.forEach((field) => {
    if (personalData[field] !== undefined) {
      personalPayload[field] = personalData[field] ?? '';
    }
  });

  return {
    // === ID系 ===
    visitor_id: getOrCreateVisitorId(),
    conversion_id: generateConversionId(), // フォーム送信時に生成
    lp_id: getLpId(),

    // === 個人情報（DBにのみ保存し、外部タグにはconversion_idのみ渡す）===
    ...personalPayload,

    // === マーケティング情報（sessionStorageから読み取り）===
    ...getUTMParams(),
    ...getAffiliateParams(),
    referrer: document.referrer ?? '',
    landing_page: window.location.href,

    // === メタ情報 ===
    submitted_at: new Date().toISOString(),
    user_agent: navigator.userAgent ?? '',
  };
}

// ============================================================
// GA4へのイベント送信
// ※個人情報はGA4に送信せず、conversion_idのみを渡す
// ============================================================
export function sendGa4Event(eventName: string, params?: Record<string, unknown>): void {
  const win = window as unknown as { gtag?: (...args: unknown[]) => void };
  if (typeof win.gtag === 'function') {
    win.gtag('event', eventName, params ?? {});
  }
}

// ============================================================
// dataLayer + GA4 両方にイベント送信
// ============================================================
export function pushDataLayerEvent(eventName: string, params?: Record<string, unknown>): void {
  // GTM dataLayer へのプッシュ
  const win = window as unknown as { dataLayer?: unknown[] };
  win.dataLayer = win.dataLayer ?? [];
  win.dataLayer.push({ event: eventName, ...params });

  // GA4 直接送信
  sendGa4Event(eventName, params);
}

// ============================================================
// GASへPOST送信（Content-Type: application/json）
// no-corsフォールバック対応
// ============================================================
export async function submitToGas(payload: Record<string, unknown>): Promise<void> {
  const body = JSON.stringify(payload);

  try {
    // まず通常fetch（CORSが設定されていれば成功）
    const res = await fetch(GAS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });

    if (res.ok) return;

    // 4xx/5xx エラーレスポンスの場合
    throw new Error(`Server responded with status: ${res.status}`);
  } catch (err) {
    const e = err as Error;

    // CORS/ネットワークエラーの場合はno-corsにフォールバック
    // TypeError "Failed to fetch" はCORSブロックまたはネットワーク障害
    const isCorsOrNetworkError =
      e.name === 'TypeError' ||
      e.message.includes('Failed to fetch') ||
      e.message.includes('NetworkError') ||
      e.message.includes('Network request failed');

    if (isCorsOrNetworkError) {
      // no-cors モード: opaque responseが返り成功/失敗判断不可
      // GASはno-corsでも受信できるため送信完了とみなす
      await fetch(GAS_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain' },
        body,
      });
      return;
    }

    // その他のエラー（4xx/5xx等）は呼び出し側でエラー表示
    throw err;
  }
}

// ============================================================
// View Tracking: 要素が画面に入った時に GA4 イベントを発火
// - data-track-view="<event_name>" 属性を持つ要素を IntersectionObserver で監視
// - 50% 以上が viewport に入った時点で発火
// - 一度発火したら以後は発火しない（per page load）
// ============================================================
const viewedElements = new WeakSet<Element>();

export function setupViewTracking(): void {
  if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !viewedElements.has(entry.target)) {
          viewedElements.add(entry.target);
          const el = entry.target as HTMLElement;
          const eventName = el.getAttribute('data-track-view') || 'view_element';
          const params: Record<string, unknown> = {
            element_type: el.getAttribute('data-element-type') || el.tagName.toLowerCase(),
            element_name: el.getAttribute('data-element-name') || el.id || '',
            page_path: window.location.pathname,
          };
          const indexAttr = el.getAttribute('data-element-index');
          if (indexAttr !== null) params.element_index = indexAttr;
          pushDataLayerEvent(eventName, params);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.5 }
  );

  // 初期 DOM の要素を監視
  document.querySelectorAll('[data-track-view]').forEach((el) => observer.observe(el));

  // 動的に追加された要素も拾うため MutationObserver で追跡
  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        const el = node as Element;
        if (el.hasAttribute && el.hasAttribute('data-track-view')) {
          observer.observe(el);
        }
        el.querySelectorAll?.('[data-track-view]').forEach((child) => observer.observe(child));
      });
    });
  });
  mutationObserver.observe(document.body, { childList: true, subtree: true });
}

// ============================================================
// Swipe Tracking: 求人カードカルーセルなどスワイプ操作に発火
// ============================================================
export function fireSwipeEvent(params: {
  card_id?: string | number;
  card_index: number;
  card_name?: string;
  carousel_name?: string;
  direction?: 'left' | 'right';
}): void {
  pushDataLayerEvent('nijikango_swipe', {
    card_id: params.card_id !== undefined ? String(params.card_id) : '',
    card_index: String(params.card_index),
    card_name: params.card_name || '',
    carousel_name: params.carousel_name || 'job_cards',
    direction: params.direction || '',
    page_path: window.location.pathname,
  });
}

// ============================================================
// 初期化処理: ページ表示時に一度だけ呼び出す
// - visitor_id の生成・保存
// - UTM/アフィリエイトパラメータをsessionStorageに保存
// - 全クリック/タップイベントのGA4トラッキング設定
// - 各セクションの view イベント設定
// ============================================================
export function initTracking(): void {
  // visitor_id を即座に生成・保存（DOMContentLoaded相当）
  getOrCreateVisitorId();

  // UTM・アフィリエイトパラメータをsessionStorageに保存
  saveParamsToSession();

  // 全ボタン・リンクのクリック/タップイベントをGA4に送信
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as HTMLElement;
      // button, a, [role="button"], [data-track] を対象
      const clickable = target.closest(
        'button, a, [role="button"], [data-track]'
      ) as HTMLElement | null;
      if (!clickable) return;

      // テキストラベルの取得（data-track > aria-label > textContent）
      const rawText =
        clickable.getAttribute('data-track') ||
        clickable.getAttribute('aria-label') ||
        clickable.textContent?.trim().substring(0, 50) ||
        'unknown';
      const label = rawText.replace(/\s+/g, ' ').trim();

      sendGa4Event('nijikango_click', {
        element_type: clickable.tagName.toLowerCase(),
        element_text: label,
        page_path: window.location.pathname,
      });
    },
    { passive: true }
  );

  // 各セクションの view イベントを設定
  setupViewTracking();
}
